import { observable, set } from 'mobx';
import {
  IInvoiceDetails,
  IFromCompanyBillingDetailsResponse,
} from '../../../api/IDataMappings';
import {
  FIELD_TYPE,
  INVOICE_CARRIER_TYPES,
  INVOICE_DONATE_MARKS,
} from '../../../constants/Constants';

const DEFAULT_BARCODE_VALUE = '/';

class InvoiceDetails {
  @observable
  public readonly attributes: IInvoiceDetails;

  constructor() {
    this.attributes = {
      [FIELD_TYPE.CORPORATE_NAME]: '',
      [FIELD_TYPE.CORPORATE_TAX_ID]: null,
      [FIELD_TYPE.IDENTIFICATION_TYPE]: 1,
      [FIELD_TYPE.INVOICE_ADDRESS]: '',
      [FIELD_TYPE.INVOICE_CARRIER_ID]: '',
      [FIELD_TYPE.INVOICE_CARRIER_TYPE]: INVOICE_CARRIER_TYPES.NONE,
      [FIELD_TYPE.INVOICE_DONATE_MARK]: INVOICE_DONATE_MARKS.NONE,
      [FIELD_TYPE.INVOICE_DONATION_LOVE_CODE]: '',
      [FIELD_TYPE.INVOICE_DONATION_NAME]: '',
      [FIELD_TYPE.INVOICE_WITH_SHIPPING]: 0,
    };
  }

  public static fromJSON(data: IInvoiceDetails): InvoiceDetails {
    const invoiceDetails = new InvoiceDetails();

    const {
      [FIELD_TYPE.INVOICE_DONATE_MARK]: invoiceDonateMark,
      [FIELD_TYPE.INVOICE_CARRIER_ID]: invoiceCarrierId,
      ...rest
    } = data;
    set(invoiceDetails.attributes, {
      ...rest,
      [FIELD_TYPE.INVOICE_DONATE_MARK]:
        invoiceDonateMark || INVOICE_DONATE_MARKS.ELECTRONIC_INVOICE,
      [FIELD_TYPE.INVOICE_CARRIER_ID]:
        invoiceCarrierId || DEFAULT_BARCODE_VALUE,
    });

    return invoiceDetails;
  }

  public toJSON(): IInvoiceDetails {
    return {
      ...this.attributes,
    };
  }

  public toCompanyBillingDetailsJSON(): IFromCompanyBillingDetailsResponse {
    return {
      [FIELD_TYPE.BILL_TO_COMPANY]:
        this.attributes[FIELD_TYPE.INVOICE_DONATE_MARK] ===
        INVOICE_DONATE_MARKS.PAPER_INVOICE,
      [FIELD_TYPE.COMPANY_ADDRESS]: this.attributes[FIELD_TYPE.INVOICE_ADDRESS],
      [FIELD_TYPE.COMPANY_NAME]: this.attributes[FIELD_TYPE.CORPORATE_NAME],
      [FIELD_TYPE.COMPANY_TAX_NUMBER]:
        this.attributes[FIELD_TYPE.CORPORATE_TAX_ID] ?? '',
    };
  }

  public static buildCorporateInvoiceDetails(
    corporateName: string,
    corporateTaxId: string,
  ): InvoiceDetails {
    const invoiceDetails = new InvoiceDetails();
    set(invoiceDetails.attributes, {
      [FIELD_TYPE.CORPORATE_NAME]: corporateName,
      [FIELD_TYPE.CORPORATE_TAX_ID]: corporateTaxId,
      [FIELD_TYPE.INVOICE_DONATE_MARK]: INVOICE_DONATE_MARKS.PAPER_INVOICE,
      [FIELD_TYPE.IDENTIFICATION_TYPE]: 2,
      [FIELD_TYPE.INVOICE_WITH_SHIPPING]: 1,
    });

    return invoiceDetails;
  }

  public static buildEInvoice(
    carrierType: INVOICE_CARRIER_TYPES,
    carrierId: string,
  ): InvoiceDetails {
    const invoiceDetails = new InvoiceDetails();
    set(invoiceDetails.attributes, {
      [FIELD_TYPE.INVOICE_CARRIER_ID]: carrierId,
      [FIELD_TYPE.INVOICE_CARRIER_TYPE]: carrierType,
      [FIELD_TYPE.INVOICE_DONATE_MARK]: INVOICE_DONATE_MARKS.ELECTRONIC_INVOICE,
    });

    return invoiceDetails;
  }

  public static buildDonationInvoice(
    donationLoveCode: string,
    donationName = '',
  ): InvoiceDetails {
    const invoiceDetails = new InvoiceDetails();
    set(invoiceDetails.attributes, {
      [FIELD_TYPE.INVOICE_DONATION_LOVE_CODE]: donationLoveCode,
      [FIELD_TYPE.INVOICE_DONATION_NAME]: donationName,
      [FIELD_TYPE.INVOICE_DONATE_MARK]: INVOICE_DONATE_MARKS.DONATION,
    });

    return invoiceDetails;
  }
}

export default InvoiceDetails;
